import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
	AbstractControl,
	FormArray,
	FormBuilder,
	FormControl,
	FormGroup,
	ReactiveFormsModule,
	ValidationErrors,
	ValidatorFn,
	Validators,
} from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DateAdapter } from '@angular/material/core';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, filter, switchMap } from 'rxjs';
import { PolishDateAdapter } from '@/common/polish-date-adapter/polish-date-adapter';
import { CURRENCIES } from '@/common/constants/currencies';
import { DateService } from '@/common/date.service';
import {
	DriverLedgerCategoryOption,
	DriverLedgerEntryLine,
	DriverLedgerFormDialogData,
	DriverLedgerFormDialogResult,
	EntryLinePayload,
	LEDGER_ENTRY_STATUSES,
	LEDGER_ENTRY_STATUS_LABEL,
	LedgerEntryStatus,
	StoreDriverLedgerPayload,
	UpdateDriverLedgerPayload,
	VehicleSearchResult,
} from '../_models/driver-ledger.model';
import { DriverLedgerApiService } from '../_api/driver-ledger.api.service';
import { CategoryFilterPipe } from './_pipes/category-filter.pipe';

type TargetCurrency = 'PLN' | 'EUR';
type EntryType = 'expense' | 'income';

interface LineFormValue {
	category: DriverLedgerCategoryOption | string | null;
	amount: number | null;
	description: string | null;
}

type LineFormGroup = FormGroup<{
	category: FormControl<DriverLedgerCategoryOption | string | null>;
	amount: FormControl<number | null>;
	description: FormControl<string | null>;
}>;

@Component({
	selector: 'app-driver-ledger-form',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatDialogModule,
		MatFormFieldModule,
		MatInputModule,
		MatButtonModule,
		MatSelectModule,
		MatDatepickerModule,
		MatButtonToggleModule,
		MatProgressSpinnerModule,
		MatAutocompleteModule,
		MatCheckboxModule,
		MatIconModule,
		MatTooltipModule,
		RouterLink,
		CategoryFilterPipe,
	],
	providers: [{ provide: DateAdapter, useClass: PolishDateAdapter }],
	templateUrl: './driver-ledger-form.component.html',
	styleUrl: './driver-ledger-form.component.scss',
})
export class DriverLedgerFormComponent implements OnInit {
	readonly isEditMode = signal(false);
	readonly showTargetCurrency = signal(false);
	readonly showCommission = signal(false);
	readonly currencies = CURRENCIES;
	readonly statuses = LEDGER_ENTRY_STATUSES;
	readonly statusLabel = LEDGER_ENTRY_STATUS_LABEL;
	readonly categories = signal<DriverLedgerCategoryOption[]>([]);
	readonly vehicleOptions = signal<VehicleSearchResult[]>([]);
	readonly gboxScanId = signal<number | null>(null);
	readonly maxDate = signal<Date>(new Date());
	readonly maxPaymentDate = signal<Date>(new Date());

	readonly linesSumPln = computed(() => {
		this.formValue();
		if (!this.form) return 0;
		const total = this.lines.controls.reduce((acc, ctrl) => {
			const v = ctrl.get('amount')?.value;
			return typeof v === 'number' && Number.isFinite(v) ? acc + v : acc;
		}, 0);
		return Math.round(total * 100) / 100;
	});

	readonly entryAmountPln = computed(() => {
		this.formValue();
		if (!this.form) return 0;
		const v = this.form.get('amount')?.value;
		return typeof v === 'number' && Number.isFinite(v) ? Math.round(v * 100) / 100 : 0;
	});

	readonly commissionPln = computed(() => {
		this.formValue();
		if (!this.form || this.form.get('type')?.value !== 'income') return 0;
		const v = this.form.get('commission')?.value;
		return typeof v === 'number' && Number.isFinite(v) ? Math.round(v * 100) / 100 : 0;
	});

	readonly linesTargetPln = computed(() => Math.round((this.entryAmountPln() - this.commissionPln()) * 100) / 100);

	readonly sumDifference = computed(() => Math.round((this.linesTargetPln() - this.linesSumPln()) * 100) / 100);
	readonly sumMatches = computed(() => this.sumDifference() === 0 && this.linesSumPln() > 0);

	form!: FormGroup;

	private readonly formValue = signal<unknown>(null);

	get lines(): FormArray<LineFormGroup> {
		return this.form.get('lines') as FormArray<LineFormGroup>;
	}

	get vehicleControl(): FormControl<VehicleSearchResult | string | null> {
		return this.form.get('vehicle') as FormControl<VehicleSearchResult | string | null>;
	}

	private readonly dialogRef = inject(MatDialogRef<DriverLedgerFormComponent>);
	private readonly data: DriverLedgerFormDialogData = inject(MAT_DIALOG_DATA);
	private readonly fb = inject(FormBuilder);
	private readonly dateService = inject(DateService);
	private readonly apiService = inject(DriverLedgerApiService);
	private readonly destroyRef = inject(DestroyRef);

	ngOnInit(): void {
		this.isEditMode.set(!!this.data.entry);
		this.gboxScanId.set(this.extractGboxScanId(this.data.entry?.description));
		this.initForm();
		this.showTargetCurrency.set(!['PLN', 'EUR'].includes(this.form.get('currency')?.value));
		this.showCommission.set(this.form.get('type')?.value === 'income');

		this.formValue.set(this.form.value);
		this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((v) => this.formValue.set(v));

		this.apiService
			.getCategories()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((categories) => this.categories.set(categories));

		this.form
			.get('vehicle')
			?.valueChanges.pipe(
				debounceTime(300),
				distinctUntilChanged(),
				filter((v): v is string => typeof v === 'string' && v.length >= 2),
				switchMap((query) => this.apiService.searchVehicles(query)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((results) => this.vehicleOptions.set(results));

		this.form
			.get('type')
			?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((type: string) => {
				this.showCommission.set(type === 'income');
				if (type !== 'income') {
					this.form.get('commission')?.setValue(null);
				}
			});

		this.form
			.get('currency')
			?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((currency: string) => {
				this.showTargetCurrency.set(!['PLN', 'EUR'].includes(currency));
				if (currency === 'EUR') {
					this.form.get('targetCurrency')?.setValue('EUR');
				} else if (currency === 'PLN') {
					this.form.get('targetCurrency')?.setValue('PLN');
				}
			});
	}

	displayCategory = (category: DriverLedgerCategoryOption | null): string => (category ? category.name : '');

	displayVehicle = (vehicle: VehicleSearchResult | null): string =>
		vehicle ? `${vehicle.name} (${vehicle.registrationNumber})` : '';

	addLine(): void {
		this.lines.push(this.createLineGroup());
		this.form.updateValueAndValidity();
	}

	removeLine(index: number): void {
		if (this.lines.length <= 1) return;
		this.lines.removeAt(index);
		this.form.updateValueAndValidity();
	}

	fillRemainderOnLine(index: number): void {
		const target = this.lines.at(index);
		if (!target) return;
		const currentLineSum = this.lines.controls.reduce((acc, ctrl, i) => {
			if (i === index) return acc;
			const v = ctrl.get('amount')?.value;
			return typeof v === 'number' && Number.isFinite(v) ? acc + v : acc;
		}, 0);
		const remainder = Math.round((this.linesTargetPln() - currentLineSum) * 100) / 100;
		target.get('amount')?.setValue(remainder > 0 ? remainder : 0);
	}

	categoryIdOf(line: AbstractControl): number | null {
		const v = line.get('category')?.value;
		return v && typeof v === 'object' ? v.id : null;
	}

	onSave(): void {
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const formValue = this.form.value;
		const entryType: EntryType = formValue.type;
		const signMultiplier = entryType === 'expense' ? -1 : 1;

		const amountInGroszy = Math.round(Math.abs(formValue.amount) * 100) * signMultiplier;
		const linesPayload = this.buildLinesPayload(formValue.lines as LineFormValue[], signMultiplier);

		const dateStr = this.formatDate(formValue.date);
		const paymentDateStr = formValue.paymentDate ? this.formatDate(formValue.paymentDate) : null;

		const vehicleValue = formValue.vehicle;
		const vehicleId = vehicleValue && typeof vehicleValue === 'object' ? vehicleValue.id : null;

		const commission =
			entryType === 'income' && formValue.commission != null ? Math.round(formValue.commission * 100) : null;
		const hasOriginalDocument = !!formValue.hasOriginalDocument;
		const documentNumber = formValue.documentNumber?.trim() ? formValue.documentNumber.trim() : null;

		if (this.isEditMode()) {
			const payload: UpdateDriverLedgerPayload = {
				date: dateStr,
				amount: amountInGroszy,
				currency: formValue.currency,
				target_currency: formValue.targetCurrency as TargetCurrency,
				lines: linesPayload,
				description: formValue.description || null,
				document_number: documentNumber,
				payment_date: paymentDateStr,
				vehicle_id: vehicleId,
				commission,
				status: formValue.status as LedgerEntryStatus,
				has_original_document: hasOriginalDocument,
			};
			this.dialogRef.close({ action: 'update', payload } as DriverLedgerFormDialogResult);
		} else {
			const payload: StoreDriverLedgerPayload = {
				date: dateStr,
				amount: amountInGroszy,
				currency: formValue.currency,
				target_currency: formValue.targetCurrency as TargetCurrency,
				lines: linesPayload,
				description: formValue.description || null,
				document_number: documentNumber,
				payment_date: paymentDateStr,
				vehicle_id: vehicleId,
				commission,
				status: formValue.status as LedgerEntryStatus,
				has_original_document: hasOriginalDocument,
			};
			this.dialogRef.close({ action: 'create', payload } as DriverLedgerFormDialogResult);
		}
	}

	onCancel(): void {
		this.dialogRef.close(null);
	}

	private buildLinesPayload(lines: LineFormValue[], signMultiplier: number): EntryLinePayload[] {
		return lines.map((line) => {
			const cat = line.category && typeof line.category === 'object' ? line.category : null;
			const amount =
				typeof line.amount === 'number' ? Math.round(Math.abs(line.amount) * 100) * signMultiplier : 0;
			return {
				category_id: cat?.id ?? null,
				amount,
				description: line.description?.trim() ? line.description.trim() : null,
			};
		});
	}

	private createLineGroup(line?: DriverLedgerEntryLine): LineFormGroup {
		const initialCategory: DriverLedgerCategoryOption | string | null =
			line?.categoryId && line?.categoryName ? { id: line.categoryId, name: line.categoryName } : null;
		const initialAmount = typeof line?.amount === 'number' ? Math.abs(line.amount) / 100 : null;
		return this.fb.group({
			category: this.fb.control<DriverLedgerCategoryOption | string | null>(initialCategory),
			amount: this.fb.control<number | null>(initialAmount, [Validators.required, Validators.min(0.01)]),
			description: this.fb.control<string | null>(line?.description ?? null),
		}) as LineFormGroup;
	}

	private initForm(): void {
		const entry = this.data.entry;
		const isIncome = entry ? entry.amount > 0 : false;
		const existingLines = entry?.lines?.length
			? entry.lines.map((l) => this.createLineGroup(l))
			: [this.createLineGroup()];

		const initialVehicle: VehicleSearchResult | string | null = entry?.vehicleId
			? {
					id: entry.vehicleId,
					name: entry.vehicleName ?? '',
					registrationNumber: entry.vehicleRegistrationNumber ?? '',
				}
			: null;

		const entryDate = entry ? new Date(entry.date) : new Date();
		const entryPaymentDate = entry?.paymentDate ? new Date(entry.paymentDate) : null;
		this.maxDate.set(latestOf(new Date(), entryDate));
		this.maxPaymentDate.set(latestOf(new Date(), entryPaymentDate));

		this.form = this.fb.group(
			{
				date: [entryDate, Validators.required],
				type: [entry ? (entry.amount < 0 ? 'expense' : 'income') : 'expense', Validators.required],
				amount: [entry ? Math.abs(entry.amount) / 100 : null, [Validators.required, Validators.min(0.01)]],
				currency: [entry?.currency ?? 'PLN', Validators.required],
				targetCurrency: [entry?.targetCurrency ?? 'PLN', Validators.required],
				description: [entry?.description ?? ''],
				paymentDate: [entryPaymentDate],
				commission: [
					isIncome && entry?.commission != null ? entry.commission / 100 : null,
					[Validators.min(0)],
				],
				status: [entry?.status ?? ('NOT_SETTLED' as LedgerEntryStatus), Validators.required],
				documentNumber: [entry?.documentNumber ?? '', Validators.maxLength(100)],
				hasOriginalDocument: [entry?.hasOriginalDocument ?? false],
				vehicle: [initialVehicle],
				lines: this.fb.array<LineFormGroup>(existingLines, [Validators.required, Validators.minLength(1)]),
			},
			{ validators: linesSumEqualsAmountValidator() },
		);
	}

	private extractGboxScanId(description: string | null | undefined): number | null {
		const match = /\[GBox scan #(\d+)\]/.exec(description ?? '');
		return match ? Number(match[1]) : null;
	}

	private formatDate(date: Date | string): string {
		if (typeof date === 'string') {
			return date.split(' ')[0];
		}
		return this.dateService.datetimeWithTimezone(date).date;
	}
}

function latestOf(today: Date, candidate: Date | null): Date {
	if (!candidate || Number.isNaN(candidate.getTime()) || candidate.getTime() <= today.getTime()) {
		return today;
	}
	return candidate;
}

function linesSumEqualsAmountValidator(): ValidatorFn {
	return (group: AbstractControl): ValidationErrors | null => {
		const amount = group.get('amount')?.value;
		const linesCtrl = group.get('lines');
		if (!(linesCtrl instanceof FormArray) || typeof amount !== 'number' || !Number.isFinite(amount)) {
			return null;
		}
		const isIncome = group.get('type')?.value === 'income';
		const commissionRaw = group.get('commission')?.value;
		const commission =
			isIncome && typeof commissionRaw === 'number' && Number.isFinite(commissionRaw) ? commissionRaw : 0;
		const target = Math.round((amount - commission) * 100) / 100;
		const sum = linesCtrl.controls.reduce((acc, line) => {
			const v = line.get('amount')?.value;
			return typeof v === 'number' && Number.isFinite(v) ? acc + v : acc;
		}, 0);
		const diff = Math.round((target - sum) * 100) / 100;
		return diff === 0
			? null
			: { sumMismatch: { targetAmount: target, linesSum: Math.round(sum * 100) / 100, diff } };
	};
}
